import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { bridgeOrganisationApi, bridgeSettingsApi, conversationCategoryApi, DEFAULT_RETRY_COUNT } from '../apis';
import { useMe } from '../organisation/useMe';
import { InternalApiBridgeSettings } from '../api-internal';
import { isFeatureEnabled, ORGANISATION_FEATURE } from '../organisation/feature/helper';

export const BRIDGE_API_CONFIGURATION_QUERY_KEY = 'bridge_api_configuration';
export const BRIDGE_FRIENDS_QUERY_KEY = 'bridge_friends';
export const BRIDGE_CONVERSATION_CATEGORIES_QUERY_KEY = 'bridge_friends';

const isBridgeRelatedFeatureEnabled = (features: string[]) => {
  return (
    isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_OUTGOING, features) ||
    isFeatureEnabled(ORGANISATION_FEATURE.BRIDGE_MESSAGING_INCOMING, features) ||
    isFeatureEnabled(ORGANISATION_FEATURE.FRAUDULENT_FUNDS_REPORTING, features)
  );
};

export const useQueryBridgeApiConfiguration = () => {
  const { currentOrganisation, features } = useMe();

  return useQuery({
    queryKey: [BRIDGE_API_CONFIGURATION_QUERY_KEY, currentOrganisation?.id],
    queryFn: async () => await bridgeSettingsApi.getBridgeSettings(),
    retry: (failureCount, error) => error.status !== 404 && failureCount < DEFAULT_RETRY_COUNT,
    enabled: isBridgeRelatedFeatureEnabled(features),
  });
};

export const useUpdateBridgeApiConfiguration = () => {
  const queryClient = useQueryClient();
  const { currentOrganisation } = useMe();

  return useMutation({
    mutationFn: async (internalApiBridgeSettings: InternalApiBridgeSettings) =>
      await bridgeSettingsApi.changeBridgeSettings({ internalApiBridgeSettings }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: [BRIDGE_API_CONFIGURATION_QUERY_KEY, currentOrganisation?.id],
      });
    },
  });
};

export const useQueryBridgeFriends = () => {
  const { currentOrganisation, features } = useMe();

  return useQuery({
    queryKey: [BRIDGE_FRIENDS_QUERY_KEY, currentOrganisation?.id],
    queryFn: async () => {
      const response = await bridgeOrganisationApi.getAllFriendOrganisations();
      return response.content;
    },
    enabled: isBridgeRelatedFeatureEnabled(features),
    initialData: [],
  });
};

export const useQueryConversationCategories = (enabled: boolean = true) => {
  const { currentOrganisation, features } = useMe();

  return useQuery({
    queryKey: [BRIDGE_CONVERSATION_CATEGORIES_QUERY_KEY, currentOrganisation?.id],
    queryFn: async () => {
      return await conversationCategoryApi.getConversationCategoriesForOrganisation();
    },
    enabled: isBridgeRelatedFeatureEnabled(features) && enabled,
    initialData: [],
  });
};
